/**
 * Calendar date helpers (pure, no React). Reykjavík (Atlantic/Reykjavik) is UTC+0 all year with
 * no DST, so a calendar date 'YYYY-MM-DD' maps 1:1 to the UTC day and wall-clock time = UTC.
 * Weeks always start on Monday. Formatting uses Intl with a cached formatter per locale+options.
 */
import { makeDateTimeFormat, makeRelativeTimeFormat, type DateFormatterLike, type RelativeFormatterLike } from './intlIs';
import { addDays, DAY_MS, isoWeekday, parseDateStr, toDateStr, TZ, weekStart } from '../../shared/time';

export { addDays, DAY_MS, isoWeekday, parseDateStr, toDateStr, TZ, weekStart };

/* ─── Calendar arithmetic ───────────────────────────────────────────────── */

/** 'YYYY-MM' → { y, m } with m = 1..12. */
export function parseYm(ym: string): { y: number; m: number } {
  const match = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!match) throw new Error(`Bad month ${ym}`);
  const y = Number(match[1]);
  const m = Number(match[2]);
  if (m < 1 || m > 12) throw new Error(`Bad month ${ym}`);
  return { y, m };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function formatYm(y: number, m: number): string {
  return `${String(y).padStart(4, '0')}-${pad2(m)}`;
}

/** 'YYYY-MM-DD' → 'YYYY-MM'. */
export function ymOf(date: string): string {
  return date.slice(0, 7);
}

/** Month arithmetic on 'YYYY-MM' (n may be negative). */
export function addMonths(ym: string, n: number): string {
  const { y, m } = parseYm(ym);
  const idx = y * 12 + (m - 1) + n;
  return formatYm(Math.floor(idx / 12), (((idx % 12) + 12) % 12) + 1);
}

export function firstOfMonth(ym: string): string {
  parseYm(ym);
  return `${ym}-01`;
}

export function daysInMonth(ym: string): number {
  const { y, m } = parseYm(ym);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/**
 * Month grid for a 'YYYY-MM': always 6 weeks × 7 days, Monday first, as 'YYYY-MM-DD' strings.
 * Includes the trailing days of the previous month and leading days of the next one.
 */
export function monthGrid(ym: string): string[][] {
  const start = weekStart(firstOfMonth(ym));
  const weeks: string[][] = [];
  for (let w = 0; w < 6; w++) {
    const week: string[] = [];
    for (let d = 0; d < 7; d++) week.push(addDays(start, w * 7 + d));
    weeks.push(week);
  }
  return weeks;
}

/** Date range covered by monthGrid(ym): first and last cell, both inclusive (like GET /api/events). */
export function monthGridRange(ym: string): { from: string; to: string } {
  const from = weekStart(firstOfMonth(ym));
  return { from, to: addDays(from, 41) };
}

/** Today's date in Reykjavík for a given instant (ms). */
export function todayStr(nowMs: number): string {
  return toDateStr(nowMs);
}

/** Whole days from `a` to `b` (b − a), both 'YYYY-MM-DD'. */
export function daysBetween(a: string, b: string): number {
  return Math.round((parseDateStr(b) - parseDateStr(a)) / DAY_MS);
}

/** 'HH:MM' (Reykjavík) of an instant. */
export function hhmmOf(ms: number): string {
  return new Date(ms).toISOString().slice(11, 16);
}

/** Minutes since midnight for 'HH:MM'. */
export function minutesOf(hhmm: string): number {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) throw new Error(`Bad time ${hhmm}`);
  return Number(m[1]) * 60 + Number(m[2]);
}

/** 'HH:MM' for minutes since midnight (wraps at 24 h). */
export function hhmmFromMinutes(total: number): string {
  const t = ((Math.round(total) % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(t / 60))}:${pad2(t % 60)}`;
}

/**
 * Duration in minutes from start to end time on a wall clock. An end at or before the start is
 * taken to be on the next day (e.g. 22:00 → 00:30 = 150 min).
 */
export function durationBetween(startHhmm: string, endHhmm: string): number {
  const d = minutesOf(endHhmm) - minutesOf(startHhmm);
  return d > 0 ? d : d + 1440;
}

/** Group items by their Reykjavík day ('YYYY-MM-DD'), preserving input order within a day. */
export function groupByDay<T>(items: readonly T[], at: (item: T) => number): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const it of items) {
    const key = toDateStr(at(it));
    const list = out.get(key);
    if (list) list.push(it);
    else out.set(key, [it]);
  }
  return out;
}

/** Check-in window: from `startsAt − checkinOpensMin` until `endsAt` (server time). */
export function checkinWindow(ev: { startsAt: number; endsAt: number; checkinOpensMin: number }): { opensAt: number; closesAt: number } {
  return { opensAt: ev.startsAt - ev.checkinOpensMin * 60_000, closesAt: ev.endsAt };
}

export function isCheckinOpen(ev: { startsAt: number; endsAt: number; checkinOpensMin: number }, nowMs: number): boolean {
  const w = checkinWindow(ev);
  return nowMs >= w.opensAt && nowMs <= w.closesAt;
}

/**
 * Date of the first occurrence a new weekly series will actually get — same rule as the server's
 * recurrence (ISO weekdays, every `intervalWeeks` weeks counted from the week of `startsOn`, slots
 * that have already started are never generated). Null when no slot falls before `untilOn`.
 */
export function firstSeriesDate(
  rule: { weekdays: readonly number[]; intervalWeeks: number; startsOn: string; untilOn?: string | null; startTime: string },
  nowMs: number,
): string | null {
  if (rule.weekdays.length === 0) return null;
  const interval = Math.max(1, rule.intervalWeeks);
  const week0 = parseDateStr(weekStart(rule.startsOn));
  const today = toDateStr(nowMs);
  const startMin = minutesOf(rule.startTime);
  let d = rule.startsOn > today ? rule.startsOn : today;
  // One full interval cycle (plus the rest of the current week) always contains a matching slot.
  for (let i = 0; i < 7 * (interval + 1); i++, d = addDays(d, 1)) {
    if (rule.untilOn && d > rule.untilOn) return null;
    if (!rule.weekdays.includes(isoWeekday(d))) continue;
    const t = parseDateStr(d);
    if (Math.floor((t - week0) / (7 * DAY_MS)) % interval !== 0) continue;
    if (t + startMin * 60_000 > nowMs) return d;
  }
  return null;
}

/* ─── Formatting ────────────────────────────────────────────────────────── */

const dtfCache = new Map<string, DateFormatterLike>();
function dtf(loc: string, opts: Intl.DateTimeFormatOptions): DateFormatterLike {
  const key = `${loc}|${JSON.stringify(opts)}`;
  let f = dtfCache.get(key);
  if (!f) {
    f = makeDateTimeFormat(loc, { timeZone: TZ, ...opts });
    dtfCache.set(key, f);
  }
  return f;
}

const rtfCache = new Map<string, RelativeFormatterLike>();
function rtf(loc: string): RelativeFormatterLike {
  let f = rtfCache.get(loc);
  if (!f) {
    f = makeRelativeTimeFormat(loc);
    rtfCache.set(loc, f);
  }
  return f;
}

type DayInput = number | string;
function toMs(d: DayInput): number {
  return typeof d === 'number' ? d : parseDateStr(d);
}

/** Capitalise the first letter (headings only — Icelandic weekday/month names are lower-case). */
export function capitalize(s: string, loc?: string): string {
  if (!s) return s;
  const first = s.codePointAt(0)!;
  const ch = String.fromCodePoint(first);
  return ch.toLocaleUpperCase(loc) + s.slice(ch.length);
}

/**
 * Joins weekday + date parts without the comma some ICU versions insert after the weekday:
 * "miðvikudagur 14. október" / "Wednesday 14 October".
 */
function joinWeekdayParts(parts: Intl.DateTimeFormatPart[]): string {
  let out = '';
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]!;
    if (p.type === 'literal' && i > 0 && parts[i - 1]!.type === 'weekday') {
      out += ' ';
      continue;
    }
    out += p.value;
  }
  return out.replace(/\s+/g, ' ').trim();
}

/** "miðvikudagur 14. október" (adds the year when `withYear`). */
export function formatDay(d: DayInput, loc: string, withYear = false): string {
  const opts: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' };
  if (withYear) opts.year = 'numeric';
  return joinWeekdayParts(dtf(loc, opts).formatToParts(toMs(d)));
}

/** "mið. 14. okt." / "Wed 14 Oct" */
export function formatShortDay(d: DayInput, loc: string): string {
  return joinWeekdayParts(dtf(loc, { weekday: 'short', day: 'numeric', month: 'short' }).formatToParts(toMs(d)));
}

/** "14. október 2026" / "14 October 2026" */
export function formatDate(d: DayInput, loc: string): string {
  return dtf(loc, { day: 'numeric', month: 'long', year: 'numeric' }).format(toMs(d));
}

/** "19:30" — 24-hour clock in both languages. */
export function formatTime(ms: number, loc: string): string {
  return dtf(loc, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(ms);
}

/** "19:30–22:00" */
export function formatTimeRange(startMs: number, endMs: number, loc: string): string {
  return `${formatTime(startMs, loc)}–${formatTime(endMs, loc)}`;
}

/** "október 2026" for a 'YYYY-MM'. */
export function formatMonthYear(ym: string, loc: string): string {
  return dtf(loc, { month: 'long', year: 'numeric' }).format(parseDateStr(firstOfMonth(ym)));
}

/** Weekday labels Monday → Sunday ("mán.", "þri." … / "Mon", "Tue" …). */
export function weekdayLabels(loc: string, style: 'long' | 'short' | 'narrow' = 'short'): string[] {
  // 2024-01-01 was a Monday.
  const monday = Date.UTC(2024, 0, 1);
  const f = dtf(loc, { weekday: style });
  return Array.from({ length: 7 }, (_, i) => f.format(monday + i * DAY_MS));
}

/** "í dag" / "á morgun" / "í gær" (or English) for −1..+1 days from today; otherwise null. */
export function relativeDayLabel(date: string, today: string, loc: string): string | null {
  const diff = daysBetween(today, date);
  if (diff < -1 || diff > 1) return null;
  return rtf(loc).format(diff, 'day');
}

/**
 * Day heading: "Í dag", "Á morgun", otherwise "Miðvikudagur 14. október" (year added when the
 * date is not in the current year). Capitalised — for headings only.
 */
export function dayHeading(date: string, today: string, loc: string): string {
  const rel = relativeDayLabel(date, today, loc);
  if (rel) return capitalize(rel, loc);
  return capitalize(formatDay(date, loc, date.slice(0, 4) !== today.slice(0, 4)), loc);
}

/**
 * Relative time for messages: "núna", "fyrir 5 mínútum", "fyrir 2 klukkustundum", "í gær";
 * older than a week → short date ("mið. 14. okt.").
 */
export function formatRelativeTime(ms: number, nowMs: number, loc: string): string {
  const diffSec = Math.round((ms - nowMs) / 1000);
  const abs = Math.abs(diffSec);
  const f = rtf(loc);
  if (abs < 45) return f.format(0, 'second');
  if (abs < 45 * 60) return f.format(Math.round(diffSec / 60), 'minute');
  if (abs < 20 * 3600) return f.format(Math.round(diffSec / 3600), 'hour');
  const days = daysBetween(toDateStr(nowMs), toDateStr(ms));
  if (Math.abs(days) <= 6) return f.format(days, 'day');
  return formatShortDay(ms, loc);
}
